import { computed, makeObservable, runInAction } from 'mobx'

import {
  IClosure,
  IRecurringWorkflowSettingsInput,
  ISitePermit,
  ISitePermitInput,
} from '~/client/graph'
import {
  DeleteClosuresDocument,
  GetFormRelatedClosuresDocument,
  SaveClosuresDocument,
} from '~/client/graph/operations/generated/Closures.generated'
import {
  GetSitePermitAggregationListDocument,
  IGetSitePermitAggregationListQueryVariables,
} from '~/client/graph/operations/generated/SitePermits.generated'

import Closure from '../../models/Closure'
import LocationBase from '../../models/LocationObjects/LocationBase'
import Workflow from '../../models/Workflow'
import { reduceByCategories } from '../../utils/util'
import BaseStoreWithById from '../baseStores/BaseWithById.store'
import { GET_FORM_RELATED_CLOSURES } from '../EventStore/eventConstants'
import InitialState from '../InitialState'
import ProjectDateStore from '../ui/ProjectDate.store'
import GraphExecutorStore from './GraphExecutor.store'
import LocationAttributesStore from './LocationAttributes.store'

export default class ClosuresStore extends BaseStoreWithById<
  Closure,
  IClosure
> {
  public constructor(
    private readonly state: InitialState,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly projectDateStore: ProjectDateStore,
  ) {
    super(Closure)
    makeObservable(this)
  }

  public get byId() {
    return this.state.closures
  }

  public async receiveOne(id: string, dto: IClosure) {
    if (dto) {
      const closure = this.updateOne(id, dto)

      if (closure.associatedPermitId) {
        await this.fetchPermits([closure.associatedPermitId])
      }
    } else {
      this.byId.delete(id)
    }
  }

  public async receiveList(dtos: IClosure[]) {
    this.clearList()

    if (!dtos?.length) {
      runInAction(() => {
        this.isDataReceived = true
      })
      return
    }

    dtos.forEach(dto => {
      if (dto) {
        this.updateOne(dto.id, dto)
      }
    })

    const permitIds = this.getUniquePermitIds(dtos)

    if (permitIds.length > 0) {
      await this.fetchPermits(permitIds)
    }

    runInAction(() => {
      this.isDataReceived = true
    })
  }

  private getUniquePermitIds(dtos: IClosure[]): string[] {
    return dtos
      .filter(dto => dto.associatedPermitId)
      .map(dto => dto.associatedPermitId)
      .filter((id, index, self) => self.indexOf(id) === index)
  }

  private async fetchPermits(permitIds: string[]) {
    if (!permitIds?.length) return

    const projectId = this.state.activeProject.id

    const variables: IGetSitePermitAggregationListQueryVariables = {
      filter: {
        ids: permitIds,
        projectId,
      },
      limit: permitIds.length,
    }

    const permitsResult = await this.graphExecutorStore.query(
      GetSitePermitAggregationListDocument,
      variables,
    )

    if (permitsResult?.data?.sitePermitsAggregation?.data) {
      const permits = permitsResult.data.sitePermitsAggregation.data

      permits.forEach(permitDto => {
        this.setPermitForClosures(permitDto)
      })
    }
  }

  private setPermitForClosures(permitDto: ISitePermit) {
    const permit = Workflow.fromDto(permitDto)

    this.list
      .filter(closure => closure.associatedPermitId === permitDto.id)
      .forEach(closure => {
        runInAction(() => {
          closure.associatedPermit = permit
        })
      })
  }

  public handlePermitChange(permitDto: ISitePermit) {
    if (!permitDto) return

    this.setPermitForClosures(permitDto)
  }

  public getLocationByClosure = (closure: Closure): LocationBase => {
    if (closure.locationId) {
      return this.locationAttributesStore.getById(closure.locationId)
    } else if (closure.associatedPermitId) {
      const associatedPermit = closure.associatedPermit
      return this.locationAttributesStore.getById(
        associatedPermit?.locations?.[0]?.id,
      )
    }
  }

  public saveClosures = async (closures: Closure[]): Promise<void> => {
    const closeDtos = closures.map(closure => closure.toDto())
    const res = await this.graphExecutorStore.mutate(SaveClosuresDocument, {
      closures: closeDtos,
    })
    if (res.error) throw res.error
    res.data?.saveManyClosures?.forEach?.(closure =>
      this.updateOne(closure.id, closure),
    )
  }

  public deleteClosures = async (closureIds: string[]): Promise<void> => {
    const res = await this.graphExecutorStore.mutate(DeleteClosuresDocument, {
      closureIds,
    })
    if (res.error) throw res.error
  }

  /** Applies closure deletions already acknowledged by another atomic command. */
  public receiveDeleted(closureIds: readonly string[]): void {
    runInAction(() => {
      for (const id of closureIds) this.byId.delete(id)
    })
  }

  public replaceClosures = async (
    existing: Closure[],
    replacements: Closure[],
  ): Promise<void> => {
    if (existing.length) {
      await this.deleteClosures(existing.map(closure => closure.id))
    }
    try {
      if (replacements.length) await this.saveClosures(replacements)
    } catch (cause: unknown) {
      if (existing.length) await this.saveClosures(existing)
      throw cause
    }
  }

  @computed
  public get closuresByLocationIdMap(): { [locationId: string]: Closure[] } {
    return reduceByCategories(this.list, closure => {
      // Key by the closure's own locationId — the same id used when saving — so
      // resource (material/equipment) closures bucket correctly even though they
      // aren't location attributes. Permit-linked closures with no locationId
      // fall back to their permit's location.
      const locationId =
        closure.locationId || this.getLocationByClosure(closure)?.id || null
      return [locationId, closure]
    })
  }

  public getLocationClosures = (id: string): Closure[] => {
    return this.closuresByLocationIdMap[id] || []
  }

  public getLocationActiveClosures = (id: string): Closure[] => {
    return (
      this.closuresByLocationIdMap[id]?.filter(
        closure =>
          closure.closureInterval.isDaily ||
          !closure.isOpenByDate(this.projectDateStore, Date.now()) ||
          closure.isAfter(Date.now()),
      ) || []
    )
  }

  public getLocationInactiveClosures = (id: string): Closure[] => {
    return (
      this.closuresByLocationIdMap[id]?.filter(
        closure =>
          !closure.closureInterval.isDaily &&
          closure.isOpenByDate(this.projectDateStore, Date.now()) &&
          !closure.isAfter(Date.now()),
      ) || []
    )
  }

  public isLocationHasClosuresWithinSelectedRange = (
    id: string,
    startDate: Date = new Date(),
    endDate?: Date,
  ): boolean => {
    const locationClosures = this.closuresByLocationIdMap[id]

    return (
      locationClosures?.length &&
      locationClosures?.some(closure =>
        closure.areWithinSelectedRange(
          this.projectDateStore,
          startDate,
          endDate || startDate,
        ),
      )
    )
  }

  public isLocationClosedForEntirePeriod = (
    id: string,
    startDate: Date = new Date(),
    endDate?: Date,
  ): boolean => {
    const locationClosures = this.closuresByLocationIdMap[id]
    const effectiveEndDate = endDate || startDate

    if (!locationClosures?.length) {
      return false
    }

    // Convert dates to timestamps for easier comparison
    const rangeStartTime = startDate.getTime()
    const rangeEndTime = effectiveEndDate.getTime()

    // Check if there's continuous coverage of closures for the entire period
    let currentTime = rangeStartTime

    // Sort closures by start date
    const sortedClosures = [...locationClosures].sort(
      (a, b) => a.closureInterval.startDate - b.closureInterval.startDate,
    )

    for (const closure of sortedClosures) {
      const closureStartTime = closure.closureInterval.startDate
      const closureEndTime = closure.closureInterval.endDate

      // If there's a gap between current time and closure start, the location isn't fully closed
      if (closureStartTime > currentTime) {
        return false
      }

      // Move the current time pointer forward if this closure extends it
      if (closureEndTime > currentTime) {
        currentTime = closureEndTime
      }

      // If we've covered the entire range, return true
      if (currentTime >= rangeEndTime) {
        return true
      }
    }

    // If we've processed all closures and haven't covered the entire range
    return false
  }

  public isLocationHasPartialClosuresWithinSelectedRange(
    locationId: string,
    startDate?: Date,
    endDate?: Date,
  ): boolean {
    return (
      this.isLocationHasClosuresWithinSelectedRange(
        locationId,
        startDate,
        endDate,
      ) && !this.isLocationClosedForEntirePeriod(locationId, startDate, endDate)
    )
  }

  public isLocationNowOpen = (id: string): boolean => {
    const locationClosures = this.closuresByLocationIdMap[id]
    const now = new Date()
    return (
      !locationClosures?.length ||
      locationClosures?.every(
        closure =>
          !closure.areWithinSelectedRange(
            this.projectDateStore,
            now,
            new Date(now.getTime() + 1),
          ),
      )
    )
  }

  public getClosuresByWorkflowType = (
    locationId: string,
    workflowType: string,
  ): Closure[] => {
    const closures = this.getLocationClosures(locationId)
    return closures.length
      ? this.filterClosuresByWorkflowType(closures, workflowType)
      : []
  }

  public isClosedForWorkflowType = (
    locationId: string,
    workflowType: string,
    startDate: Date,
    endDate: Date,
  ): boolean => {
    if (!startDate || !endDate) {
      return false
    }

    const filteredClosures = this.getClosuresByWorkflowType(
      locationId,
      workflowType,
    )

    if (!filteredClosures.length) {
      return false
    }

    return filteredClosures.some(closure =>
      this.projectDateStore.isClosedBetween(
        startDate,
        endDate,
        [],
        [closure.closureInterval],
        'partially',
      ),
    )
  }

  public getRelatedClosuresByDates = async (
    permit: ISitePermitInput,
    recurringSettings?: IRecurringWorkflowSettingsInput,
  ): Promise<IClosure[]> => {
    runInAction(() => {
      this.state.loading.set(GET_FORM_RELATED_CLOSURES, true)
    })

    // Returns closures that intersect with the dates of the permit or recurring settings
    const result = await this.graphExecutorStore.query(
      GetFormRelatedClosuresDocument,
      {
        permit,
        recurringSettings,
        timezoneId: this.state.activeProject.timezoneId,
      },
    )

    runInAction(() => {
      this.state.loading.set(GET_FORM_RELATED_CLOSURES, false)
    })

    return result?.data?.getFormRelatedClosures || []
  }

  public validateIntersectedClosures = (
    closures: IClosure[],
    locationIds: string[],
    workflowType: string,
  ): string[] => {
    if (!locationIds?.length || !closures?.length) {
      return []
    }

    return locationIds.filter(locationId => {
      const locationClosures = closures
        .filter(closure => closure.locationId === locationId)
        .map(closureDto => Closure.fromDto(closureDto))

      if (!locationClosures.length) return false

      const filteredClosures = this.filterClosuresByWorkflowType(
        locationClosures,
        workflowType,
      )

      return !!filteredClosures.length
    })
  }

  private filterClosuresByWorkflowType = (
    closures: Closure[],
    workflowType: string,
  ): Closure[] => {
    return closures.filter(closure => {
      if (!closure.specifiedPermitTypes?.length) {
        return true
      }
      return (
        !workflowType || closure.specifiedPermitTypes.includes(workflowType)
      )
    })
  }
}
